import type { Locale, OperationalPaymentContract } from "@chams-pay/contracts";
import { normalizeSchoolLevelLabel, operationsMessages, translate } from "@chams-pay/contracts";
import { Badge } from "@chams-pay/design-system/components/badge";
import { Button, buttonVariants } from "@chams-pay/design-system/components/button";
import { Checkbox } from "@chams-pay/design-system/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@chams-pay/design-system/components/dialog";
import { Label } from "@chams-pay/design-system/components/label";
import { FinancialDocument, printFinancialDocument } from "@chams-pay/document-ui";
import { Copy, Download, LoaderCircle, MessageCircle, Printer } from "lucide-react";
import { casablancaDate } from "../core/format.js";
import type { OperationalPaymentController } from "../core/use-operations.js";
import { useOperationalPayment } from "../core/use-operations.js";
import { A4DocumentPreview } from "../ui/a4-document-preview.js";
import { DatePicker } from "../ui/date-picker.js";
import { Amount, Bidi, DateValue } from "../ui/figures.js";
import { ScopedError } from "../ui/states.js";
import { ReceiptLanguageSwitch } from "./receipt-language-switch.js";

/* ------------------------------------------------------------------ */
/* Synchronized payment: document chrome, sharing and cancellation      */
/* ------------------------------------------------------------------ */

const lifecycleKey = {
  pending: "ops.pending",
  settled: "ops.settled",
  rejected: "ops.rejected",
  cancelled: "ops.cancelled",
} as const satisfies Record<
  OperationalPaymentContract["lifecycle"],
  Parameters<typeof translate>[1]
>;

function lifecycleVariant(
  lifecycle: OperationalPaymentContract["lifecycle"],
): "success" | "warning" | "destructive" {
  if (lifecycle === "settled") return "success";
  if (lifecycle === "pending") return "warning";
  return "destructive";
}

function CancelDialog({
  locale,
  payment,
  controller,
}: {
  readonly locale: Locale;
  readonly payment: OperationalPaymentContract;
  readonly controller: OperationalPaymentController;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const blocked = controller.cancelDisabled || controller.cancellationDate.length === 0;

  return (
    /* A destructive decision demands an explicit choice: neither Esc nor an
       outside click dismisses this dialog. */
    <Dialog
      open={controller.showCancellation}
      onOpenChange={(open) => {
        if (open) controller.setShowCancellation(true);
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="payment-cancel-dialog flex max-h-[88dvh] flex-col gap-0 overflow-hidden p-0 ring-destructive-border sm:max-w-lg"
        data-testid="payment-cancel-dialog"
      >
        <DialogHeader className="flex-row items-start gap-3 border-b border-destructive-border bg-destructive-soft p-5">
          <span
            aria-hidden="true"
            className="grid size-9 shrink-0 place-items-center rounded-full bg-destructive font-heading text-base font-extrabold text-white"
          >
            !
          </span>
          <span className="flex flex-col gap-1">
            <DialogTitle className="text-base text-destructive">{t("ops.cancelTitle")}</DialogTitle>
            <DialogDescription className="text-[11.5px] leading-relaxed text-destructive">
              {t("ops.cancelWarn")}
            </DialogDescription>
          </span>
        </DialogHeader>

        <form
          id="payment-cancel-form"
          className="flex flex-1 flex-col gap-4 overflow-y-auto p-5"
          onSubmit={(event) => {
            event.preventDefault();
            void controller.cancelPayment();
          }}
        >
          <dl className="flex flex-col gap-2 rounded-lg border border-border bg-surface-2 p-3.5 text-[11.5px]">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t("ops.reference")}</dt>
              <dd>
                <Bidi className="font-bold">{payment.payment.reference}</Bidi>
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t("ops.amount")}</dt>
              <dd>
                <Amount value={payment.payment.amountMad} locale={locale} className="font-bold" />
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t("ops.student")}</dt>
              <dd className="font-heading font-bold">
                {locale === "ar"
                  ? payment.document.student.nameAr
                  : payment.document.student.nameFr}
              </dd>
            </div>
          </dl>

          <Label className="flex flex-col items-stretch gap-1.5 text-start">
            <span className="font-heading text-[11.5px] font-bold text-foreground/75">
              {t("ops.cancelDate")} <span className="text-destructive">*</span>
            </span>
            <DatePicker
              locale={locale}
              label={t("ops.cancelDate")}
              max={casablancaDate()}
              value={controller.cancellationDate}
              onChange={controller.setCancellationDate}
            />
          </Label>

          <Label className="flex flex-col items-stretch gap-1.5 text-start">
            <span className="font-heading text-[11.5px] font-bold text-foreground/75">
              {t("ops.cancelReason")} <span className="text-destructive">*</span>
            </span>
            <textarea
              className="min-h-24 rounded-md border border-input bg-card px-3 py-2 text-sm"
              value={controller.cancellationReason}
              minLength={3}
              maxLength={500}
              required
              onChange={(event) => controller.setCancellationReason(event.target.value)}
            />
          </Label>

          <div className="cancellation-confirmation flex min-h-11 items-start gap-2 text-start text-xs font-semibold">
            <Checkbox
              className="mt-0.5"
              aria-label={t("ops.confirmCancellationCheck")}
              checked={controller.confirmed}
              onCheckedChange={controller.setConfirmed}
            />
            <span>{t("ops.confirmCancellationCheck")}</span>
          </div>

          {controller.actionFailed && (
            <ScopedError locale={locale} onRetry={() => void controller.cancelPayment()} />
          )}
        </form>

        <div className="flex flex-wrap items-center gap-2.5 border-t border-destructive-border bg-surface-2 p-4">
          <Button
            type="button"
            variant="outline"
            className="min-h-11"
            onClick={() => controller.setShowCancellation(false)}
          >
            {t("ops.keepPayment")}
          </Button>
          <Button
            type="submit"
            form="payment-cancel-form"
            variant="destructive-solid"
            className="ms-auto min-h-11"
            disabled={blocked}
          >
            {t("ops.confirmCancel")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function OperationalPaymentSurface({
  locale,
  payment,
  csrf,
  onOpenChange,
  onChanged,
}: {
  readonly locale: Locale;
  readonly payment: OperationalPaymentContract | null;
  readonly csrf: string;
  readonly onOpenChange: (open: boolean) => void;
  readonly onChanged: () => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const controller = useOperationalPayment({
    payment,
    csrf,
    onClose: () => onOpenChange(false),
    onChanged,
  });
  const cancelled = payment?.lifecycle === "cancelled";

  return (
    <>
      <Dialog open={payment !== null} onOpenChange={onOpenChange}>
        <DialogContent
          className="payment-history-surface flex max-h-[90dvh] flex-col gap-0 overflow-hidden p-0 sm:max-w-5xl"
          closeLabel={t("common.close")}
          data-testid="operational-payment-surface"
        >
          {payment && (
            <>
              <DialogHeader className="flex-row flex-wrap items-center gap-4 border-b border-border/70 p-5 pe-14">
                <span className="flex min-w-0 flex-col gap-1">
                  <span className="flex flex-wrap items-baseline gap-3">
                    <DialogTitle
                      className={`chams-figure text-[19px] font-extrabold ${
                        cancelled ? "text-muted-foreground line-through" : ""
                      }`}
                    >
                      <bdi dir="ltr">{payment.payment.reference}</bdi>
                    </DialogTitle>
                    <Badge variant={lifecycleVariant(payment.lifecycle)} dot>
                      {t(lifecycleKey[payment.lifecycle])}
                    </Badge>
                  </span>
                  <DialogDescription className="text-[11.5px]">
                    {locale === "ar"
                      ? payment.document.student.nameAr
                      : payment.document.student.nameFr}{" "}
                    · <Bidi>{normalizeSchoolLevelLabel(payment.document.student.classLabel)}</Bidi>{" "}
                    ·{" "}
                    {t(
                      payment.document.method.method === "cash"
                        ? "ops.cash"
                        : payment.document.method.method === "cheque"
                          ? "ops.cheque"
                          : "ops.transfer",
                    )}{" "}
                    · <DateValue value={payment.document.receivedDate} />
                  </DialogDescription>
                </span>
              </DialogHeader>

              <div className="payment-history-document flex flex-1 flex-col overflow-hidden lg:flex-row">
                <div
                  className="flex-1 overflow-y-auto bg-sunken p-6"
                  data-document-preview-canvas
                  dir="ltr"
                >
                  <div className="animate-rise mx-auto w-full max-w-[28rem]">
                    <A4DocumentPreview locale={controller.receiptLocale} spatialDirection="ltr">
                      <FinancialDocument
                        locale={controller.receiptLocale}
                        snapshot={payment.document}
                        lifecycle={payment.lifecycle}
                        {...(payment.settlementDate
                          ? { settlementDate: payment.settlementDate }
                          : {})}
                        {...(payment.cancellation
                          ? { cancellationReason: payment.cancellation.reason }
                          : {})}
                      />
                    </A4DocumentPreview>
                  </div>
                </div>

                <aside className="flex w-full flex-none flex-col gap-4 overflow-y-auto border-t border-border/70 p-5 lg:w-[330px] lg:border-s lg:border-t-0">
                  <ReceiptLanguageSwitch
                    locale={locale}
                    value={controller.receiptLocale}
                    onChange={controller.setReceiptLocale}
                  />

                  <div className="flex flex-col gap-2.5">
                    <Button
                      type="button"
                      className="h-[46px] min-h-11 w-full"
                      onClick={printFinancialDocument}
                    >
                      <Printer aria-hidden="true" />
                      {t("ops.print")}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      className="min-h-11 w-full"
                      disabled={!controller.official}
                      onClick={() => void controller.download()}
                    >
                      <Download aria-hidden="true" />
                      {t("ops.download")}
                    </Button>
                    {controller.shareable && !cancelled && (
                      <>
                        {controller.whatsapp.url ? (
                          <a
                            href={controller.whatsapp.url}
                            target="_blank"
                            rel="external noopener noreferrer"
                            className={buttonVariants({
                              variant: "outline",
                              className:
                                "min-h-11 w-full !border-success bg-white text-success hover:!border-success hover:!bg-success-soft hover:!text-success",
                            })}
                          >
                            <MessageCircle aria-hidden="true" />
                            {operationsMessages(locale).whatsapp}
                          </a>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            className="min-h-11 w-full border-success bg-white text-success hover:!border-success hover:!bg-success-soft hover:!text-success"
                            disabled={!csrf || controller.whatsapp.preparing}
                            onClick={controller.whatsapp.prepare}
                          >
                            {controller.whatsapp.preparing ? (
                              <LoaderCircle aria-hidden="true" className="animate-spin" />
                            ) : (
                              <MessageCircle aria-hidden="true" />
                            )}
                            {controller.whatsapp.preparing
                              ? operationsMessages(locale).whatsappPreparing
                              : operationsMessages(locale).whatsappGenerate}
                          </Button>
                        )}
                        {controller.whatsapp.url && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full"
                            onClick={() => void controller.whatsapp.copy()}
                          >
                            <Copy aria-hidden="true" />
                            {controller.whatsapp.copied
                              ? operationsMessages(locale).whatsappCopied
                              : operationsMessages(locale).whatsappCopy}
                          </Button>
                        )}
                        {controller.whatsapp.failed && (
                          <small role="alert" className="text-center text-destructive">
                            {operationsMessages(locale).whatsappPrepareFailed}{" "}
                            <button
                              type="button"
                              className="font-bold underline underline-offset-2"
                              onClick={controller.whatsapp.retry}
                            >
                              {operationsMessages(locale).whatsappRetry}
                            </button>
                          </small>
                        )}
                        {controller.whatsapp.copyFailed && (
                          <small role="alert" className="text-center text-destructive">
                            {operationsMessages(locale).whatsappCopyFailed}
                          </small>
                        )}
                      </>
                    )}
                  </div>

                  {payment.cancellation && (
                    <div className="cancellation-audit-context flex flex-col gap-1 rounded-lg border border-destructive-border bg-destructive-soft p-3.5 text-[11.5px] text-destructive">
                      <strong className="font-heading">{t("ops.cancelledOn")}</strong>
                      <DateValue value={payment.cancellation.date} />
                      <span>
                        {t("ops.cancelledBy")}{" "}
                        {locale === "ar"
                          ? payment.cancellation.actorNameAr
                          : payment.cancellation.actorNameFr}
                      </span>
                      <span>{payment.cancellation.reason}</span>
                    </div>
                  )}

                  {controller.actionFailed && !controller.showCancellation && (
                    <ScopedError locale={locale} />
                  )}

                  {payment.canCancel && (
                    <div className="mt-auto flex flex-col border-t border-border/70 pt-4">
                      <Button
                        type="button"
                        variant="destructive"
                        className="h-[46px] min-h-11 w-full"
                        onClick={() => controller.setShowCancellation(true)}
                      >
                        {t("ops.cancelPayment")}
                      </Button>
                    </div>
                  )}
                </aside>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {payment && payment.canCancel && (
        <CancelDialog locale={locale} payment={payment} controller={controller} />
      )}
    </>
  );
}
